import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
  useMediaQuery, useTheme,
} from '@mui/material';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import AssignmentTurnedInRounded from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import PrecisionManufacturingRounded from '@mui/icons-material/PrecisionManufacturingRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';

import {
  listInkTrolleyRuns, getInkTrolleyRun, reconcileInkTrolleyRun,
  cancelInkTrolleyRun, setInkTrolleyRunStatus, listInkLocations,
  InkTrolleyRun, InkReconcileResult, InkTrolleyUnit,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';
import { fmtKg, fmtMoney, fmtDateTime, errMsg } from './inkTrolley/kitFormat';
import { useInkInvalidate } from './inkTrolley/useInkInvalidate';


const STATUS_STYLE: Record<string, { label: string; fg: string; bg: string }> = {
  kitted:    { label: 'KITTED',    fg: TOKENS.accentStrong, bg: `${TOKENS.accent}1F` },
  at_press:  { label: 'AT PRESS',  fg: '#1e40af',           bg: '#1e40af1F' },
  returned:  { label: 'RETURNED',  fg: TOKENS.good,         bg: `${TOKENS.good}1F` },
  cancelled: { label: 'CANCELLED', fg: TOKENS.ink3,         bg: TOKENS.lineSoft },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? { label: status.toUpperCase(), fg: TOKENS.ink2, bg: TOKENS.lineSoft };
  return (
    <Chip size="small" label={s.label}
      sx={{ height: 20, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, color: s.fg, bgcolor: s.bg }} />
  );
}

function Loading() {
  return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>;
}

/**
 * InkTrolleyView — gang-run ink kitting.
 *
 * A trolley run is one kitting session: `kitted` → `at_press` → `returned`.
 * Kitting DRAWS ink from the store onto a trolley — a transfer, not a
 * depletion; the ink is still ours. Reconciliation is where it counts: the
 * operator says how much came back, the backend books `issued − returned` as a
 * FIFO depletion across the batch layers on the trolley, and that is the moment
 * inventory value actually leaves the business. Usage belongs to the RUN, not
 * to any one job — ink flow across a continuous gang run can't be apportioned.
 */
export default function InkTrolleyView() {
  const navigate = useNavigate();
  const [reconcileId, setReconcileId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const invalidate = useInkInvalidate();

  const openRunsQ = useQuery({
    queryKey: productionKeys.inkTrolleyRuns('open'),
    queryFn: () => listInkTrolleyRuns('open'),
    staleTime: 30 * 1000,
  });

  const allRunsQ = useQuery({
    queryKey: productionKeys.inkTrolleyRuns(),
    queryFn: () => listInkTrolleyRuns(),
    staleTime: 60 * 1000,
  });

  const locationsQ = useQuery({
    queryKey: productionKeys.inkLocations(),
    queryFn: listInkLocations,
    staleTime: 60 * 1000,
  });

  const markAtPress = useMutation({
    mutationFn: (id: number) => setInkTrolleyRunStatus(id, 'at_press'),
    onSuccess: (run) => { setActionError(null); invalidate(run.id); },
    onError: (err) => setActionError(errMsg(err)),
  });

  const cancelRun = useMutation({
    mutationFn: (id: number) => cancelInkTrolleyRun(id),
    onSuccess: (res) => { setActionError(null); invalidate(res.run_id); },
    onError: (err) => setActionError(errMsg(err)),
  });

  const openRuns = openRunsQ.data ?? [];
  const history = useMemo(
    () => (allRunsQ.data ?? [])
      .filter((r) => r.status === 'returned' || r.status === 'cancelled')
      .sort((a, b) => (b.returned_at ?? b.issued_at).localeCompare(a.returned_at ?? a.issued_at))
      .slice(0, 25),
    [allRunsQ.data],
  );

  const trolleys = useMemo(
    () => (locationsQ.data ?? []).filter((l) => l.location_type === 'trolley' && l.active),
    [locationsQ.data],
  );
  const freeTrolleys = trolleys.filter((t) => t.open_run_id == null).length;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <LocalShippingRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink trolley</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2 }}>
        Kit a gang run onto a trolley — that’s a transfer out of the store, nothing is consumed yet.
        When the trolley comes back, reconcile it: what didn’t return is what the run actually used.
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {/* ---------- OPEN RUNS (the hero) ---------- */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Open runs</Typography>
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
            {openRuns.length} out on the floor · {freeTrolleys} of {trolleys.length} trolleys free
          </Typography>
        </Stack>
        <Button size="small" variant="contained" startIcon={<AddRounded />}
          onClick={() => navigate('/production/ink-trolley/kit')} disabled={trolleys.length === 0}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Kit a trolley
        </Button>
      </Stack>

      {openRunsQ.isLoading ? <Loading /> : openRunsQ.isError ? (
        <Alert severity="error" sx={{ mb: 3 }}>Couldn’t load open runs: {errMsg(openRunsQ.error)}</Alert>
      ) : openRuns.length === 0 ? (
        <Box sx={{
          border: `1px dashed ${TOKENS.line}`, borderRadius: '10px', p: 4, mb: 3,
          textAlign: 'center', color: TOKENS.ink3,
        }}>
          <Typography sx={{ fontSize: 13.5 }}>No trolleys out. Every trolley is back in the store.</Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid', gap: 1.5, mb: 3,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}>
          {openRuns.map((run) => (
            <OpenRunCard key={run.id} run={run}
              busy={(markAtPress.isPending && markAtPress.variables === run.id)
                || (cancelRun.isPending && cancelRun.variables === run.id)}
              onReconcile={() => setReconcileId(run.id)}
              onMarkAtPress={() => markAtPress.mutate(run.id)}
              onCancel={() => {
                if (window.confirm(
                  `Cancel run #${run.id} on ${run.trolley_code}? All ${fmtKg(run.issued_kg)} goes back to the store and nothing is charged to the run.`,
                )) cancelRun.mutate(run.id);
              }}
            />
          ))}
        </Box>
      )}

      {/* ---------- HISTORY ---------- */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <HistoryRounded sx={{ color: TOKENS.ink3, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Recent closed runs</Typography>
      </Stack>

      {allRunsQ.isLoading ? <Loading /> : allRunsQ.isError ? (
        <Alert severity="error">Couldn’t load run history: {errMsg(allRunsQ.error)}</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Run', 'Trolley', 'Jobs', 'Press', 'Status', 'Issued', 'Used', 'Value used', 'Closed'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((r) => (
                <TableRow key={r.id} hover sx={{ opacity: r.status === 'cancelled' ? 0.55 : 1 }}>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>#{r.id}</TableCell>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{r.trolley_code}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(r.job_numbers ?? []).map((j) => (
                        <Chip key={j} size="small" label={j}
                          sx={{ height: 18, fontSize: 10.5, fontFamily: TOKENS.monoFont, fontWeight: 700, bgcolor: TOKENS.lineSoft }} />
                      ))}
                      {(r.job_numbers ?? []).length === 0 && (
                        <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>—</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>
                    {r.press_code ?? '—'}
                    {r.family_run_id != null && (
                      <Box component="span" sx={{ ml: 0.75, fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink3 }}>
                        run #{r.family_run_id}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: TOKENS.ink2 }}>
                    {fmtKg(r.issued_kg)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {r.status === 'cancelled' ? '—' : fmtKg(r.used_kg)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: TOKENS.ink2 }}>
                    {r.status === 'cancelled' ? '—' : fmtMoney(r.used_value)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: TOKENS.ink3, whiteSpace: 'nowrap' }}>
                    {fmtDateTime(r.returned_at ?? r.issued_at)}
                  </TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow><TableCell colSpan={9} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  No runs have been reconciled yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {reconcileId != null && (
        <ReconcileDialog runId={reconcileId} onClose={() => setReconcileId(null)} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

function OpenRunCard({ run, busy, onReconcile, onMarkAtPress, onCancel }: {
  run: InkTrolleyRun; busy: boolean;
  onReconcile: () => void; onMarkAtPress: () => void; onCancel: () => void;
}) {
  return (
    <Box sx={{
      border: `1px solid ${TOKENS.line}`, borderLeft: `4px solid ${TOKENS.accent}`,
      borderRadius: '10px', p: 1.75, bgcolor: TOKENS.panel, boxShadow: TOKENS.shadowSm,
      display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 16 }}>
            {run.trolley_code}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>run #{run.id}</Typography>
        </Stack>
        <StatusChip status={run.status} />
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {(run.job_numbers ?? []).map((j) => (
          <Chip key={j} size="small" label={j}
            sx={{ height: 20, fontSize: 11, fontFamily: TOKENS.monoFont, fontWeight: 700, bgcolor: TOKENS.lineSoft }} />
        ))}
        {(run.job_numbers ?? []).length === 0 && (
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>No job numbers recorded</Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{
        px: 1, py: 0.75, borderRadius: '8px', bgcolor: TOKENS.lineSoft,
      }}>
        <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
          On the trolley
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: TOKENS.accent, fontVariantNumeric: 'tabular-nums' }}>
          {fmtKg(run.issued_kg)}
        </Typography>
      </Stack>

      <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, lineHeight: 1.6 }}>
        {run.press_code ? `Press ${run.press_code}` : 'Press —'}
        {run.family_run_id != null ? ` · Family run #${run.family_run_id}` : ''}
        <br />
        Issued {fmtDateTime(run.issued_at)}
      </Typography>

      <Stack direction="row" spacing={0.75} sx={{ mt: 'auto', pt: 0.5 }}>
        <Button size="small" variant="contained" fullWidth disabled={busy}
          startIcon={<AssignmentTurnedInRounded />} onClick={onReconcile}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Reconcile
        </Button>
        {run.status === 'kitted' && (
          <Tooltip title="Mark this trolley as delivered to the press">
            <span>
              <IconButton size="small" disabled={busy} onClick={onMarkAtPress}
                sx={{ border: `1px solid ${TOKENS.line}`, borderRadius: '8px' }}>
                <PrecisionManufacturingRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Kitted in error — put it all back, consume nothing">
          <span>
            <IconButton size="small" disabled={busy} onClick={onCancel}
              sx={{ border: `1px solid ${TOKENS.line}`, borderRadius: '8px' }}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------

/**
 * ReconcileDialog — the moment that matters.
 *
 * The trolley is back. Scan every sealed tub as it goes on the shelf; whatever
 * ISN'T scanned back is what the run consumed, and the backend books it as a
 * FIFO depletion across the batch layers on the trolley. That is the only point
 * at which inventory value leaves the business — so the consumed total is the
 * loudest thing on this screen, and it counts UP as you scan, not down.
 *
 * Same shape as the kit page (scan bar, tiles, green ticks) because it is the
 * same physical job in reverse, done by the same person on the same tablet.
 */
function ReconcileDialog({ runId, onClose }: { runId: number; onClose: () => void }) {
  const invalidate = useInkInvalidate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [scanned, setScanned] = useState<Set<string>>(new Set());
  const [scanInput, setScanInput] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [result, setResult] = useState<InkReconcileResult | null>(null);

  const runQ = useQuery({
    queryKey: productionKeys.inkTrolleyRun(runId),
    queryFn: () => getInkTrolleyRun(runId),
  });

  // The sealed tubs kitted onto this run — the checklist to scan back onto the
  // shelf. Opened tubs never come back; they were used at the press.
  const kitted = useMemo<InkTrolleyUnit[]>(
    () => (runQ.data?.units ?? []).filter((u) => u.status === 'sealed'),
    [runQ.data],
  );
  const byCode = useMemo(() => new Map(kitted.map((u) => [u.unit_code, u])), [kitted]);
  const byInk = useMemo(() => {
    const m = new Map<string, InkTrolleyUnit[]>();
    kitted.forEach((u) => { const a = m.get(u.ink_key) ?? []; a.push(u); m.set(u.ink_key, a); });
    return Array.from(m.entries());
  }, [kitted]);

  const reconcile = useMutation({
    // No returned_by in the payload: the backend stamps it from the session
    // (_actor in blueprints/ink_stock.py). Jack, 11/09 — "we should be able to
    // infer this from who is logged in".
    mutationFn: (payload: { mode: 'scan'; returned_unit_codes: string[] }) =>
      reconcileInkTrolleyRun(runId, payload),
    onSuccess: (res) => { setResult(res); invalidate(runId); },
  });

  /** Same suffix tolerance as the kit page — a scuffed label still resolves. */
  const addScan = (raw: string) => {
    const code = raw.trim().toUpperCase();
    setScanInput('');
    if (!code) return;
    let hit = byCode.get(code);
    if (!hit) {
      const matches = kitted.filter((u) => u.unit_code.endsWith(code));
      if (matches.length > 1) {
        setFeedback({ tone: 'bad', text: `"${code}" matches ${matches.length} tubs on this run — scan the full code.` });
        return;
      }
      hit = matches[0];
    }
    if (!hit) {
      setFeedback({ tone: 'bad', text: `${code} isn’t a sealed tub on this run.` });
      return;
    }
    if (scanned.has(hit.unit_code)) {
      setFeedback({ tone: 'bad', text: `${hit.unit_code} is already back on the shelf.` });
      return;
    }
    setFeedback({ tone: 'ok', text: `${hit.unit_code} · ${hit.ink_key} back on the shelf` });
    setScanned((s) => new Set(s).add(hit!.unit_code));
  };

  const toggle = (code: string) =>
    setScanned((s) => {
      const n = new Set(s);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });

  const consumed = kitted.filter((u) => !scanned.has(u.unit_code));
  const consumedKg = consumed.reduce((s, u) => s + Number(u.nominal_kg), 0);

  // ---- the payoff ----
  if (result) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ fontWeight: 800 }}>Run #{result.run_id} reconciled</DialogTitle>
        <DialogContent>
          <Box sx={{
            p: 2.5, mb: 2, borderRadius: '14px', textAlign: 'center',
            bgcolor: `${TOKENS.accent}12`, border: `1px solid ${TOKENS.accent}44`,
          }}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: TOKENS.ink3 }}>
              Ink consumed by this gang run
            </Typography>
            <Typography sx={{ fontSize: 38, fontWeight: 800, color: TOKENS.accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
              {fmtKg(result.total_used_kg)}
            </Typography>
            <Typography sx={{ fontSize: 19, fontWeight: 700, color: TOKENS.ink2, fontVariantNumeric: 'tabular-nums' }}>
              {fmtMoney(result.total_used_value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.ink3, mt: 0.75 }}>
              Opened tubs are used in full; the sealed remainder is back at STORE.
            </Typography>
          </Box>

          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Ink', 'Issued', 'Opened', 'At press', 'Used'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.usage.map((u) => (
                <TableRow key={u.ink_key}>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{u.ink_key}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: TOKENS.ink2, fontVariantNumeric: 'tabular-nums' }}>{u.issued_tubs} tubs</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: TOKENS.ink2, fontVariantNumeric: 'tabular-nums' }}>{u.opened_tubs} tubs</TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
                    color: (u.unscanned_tubs ?? 0) > 0 ? TOKENS.warn : TOKENS.ink2 }}>
                    {u.press_scanned_tubs ?? 0}/{u.opened_tubs} scanned
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtKg(u.used_kg)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* The traceability gap — used, but never scanned onto a job at the press,
              so the supplier batch is tied to the run only. Named, not hidden. */}
          {(result.unscanned ?? []).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                {result.unscanned!.length} tub{result.unscanned!.length === 1 ? '' : 's'} used without a press scan
              </Typography>
              <Typography sx={{ fontSize: 12, mb: 0.75 }}>
                Their supplier batch is recorded against this run, not a job. For traceability the
                press should scan every tub as it goes on.
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {result.unscanned!.map((t) => (
                  <Chip key={t.unit_code} size="small"
                    label={`${t.unit_code} · ${t.ink_key}${t.supplier_batch_code ? ` · ${t.supplier_batch_code}` : ''}`}
                    sx={{ height: 20, fontSize: 10.5, fontFamily: TOKENS.monoFont, bgcolor: 'rgba(0,0,0,0.05)' }} />
                ))}
              </Stack>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="large" variant="contained" onClick={onClose}
            sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
        Return {runQ.data?.trolley_code ?? 'trolley'} · run #{runId}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2 }}>
          Scan every sealed tub as you put it back. Whatever isn’t scanned back is what the run
          used — the only moment ink leaves stock.
        </Typography>

        {runQ.isLoading ? <Loading /> : runQ.isError ? (
          <Alert severity="error">Couldn’t load the trolley: {errMsg(runQ.error)}</Alert>
        ) : kitted.length === 0 ? (
          <Alert severity="info">
            No sealed tubs came back on this trolley — everything issued was opened at the press.
          </Alert>
        ) : (
          <Stack spacing={1.75}>
            {reconcile.isError && <Alert severity="error">{errMsg(reconcile.error)}</Alert>}

            {(runQ.data?.job_numbers ?? []).length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(runQ.data?.job_numbers ?? []).map((j) => (
                  <Chip key={j} size="small" label={j}
                    sx={{ height: 20, fontSize: 11, fontFamily: TOKENS.monoFont, fontWeight: 700, bgcolor: TOKENS.lineSoft }} />
                ))}
              </Stack>
            )}

            <Box>
              <TextField
                fullWidth autoFocus value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addScan(scanInput); } }}
                placeholder="Scan a returned tub"
                InputProps={{
                  startAdornment: <QrCodeScannerRounded sx={{ mr: 1, color: TOKENS.ink3 }} />,
                  sx: { fontFamily: TOKENS.monoFont, fontSize: 17, fontWeight: 700, borderRadius: '12px' },
                }}
              />
              {feedback && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{
                  mt: 1, p: 1, borderRadius: '10px',
                  bgcolor: feedback.tone === 'ok' ? `${TOKENS.good}14` : `${TOKENS.bad}14`,
                  border: `1px solid ${feedback.tone === 'ok' ? TOKENS.good : TOKENS.bad}33`,
                }}>
                  {feedback.tone === 'ok'
                    ? <CheckCircleRounded sx={{ color: TOKENS.good, fontSize: 20 }} />
                    : <ErrorOutlineRounded sx={{ color: TOKENS.bad, fontSize: 20 }} />}
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: feedback.tone === 'ok' ? TOKENS.good : TOKENS.bad }}>
                    {feedback.text}
                  </Typography>
                </Stack>
              )}
              <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 0.75 }}>
                Or tap a tub below to mark it back on the shelf.
              </Typography>
            </Box>

            {byInk.map(([ink, tubs]) => {
              const back = tubs.filter((t) => scanned.has(t.unit_code)).length;
              const allBack = back === tubs.length;
              return (
                <Box key={ink} sx={{
                  p: 1.5, borderRadius: '14px',
                  border: `1px solid ${allBack ? TOKENS.good : TOKENS.line}`,
                  borderLeft: `6px solid ${allBack ? TOKENS.good : TOKENS.accent}`,
                  bgcolor: allBack ? `${TOKENS.good}0F` : TOKENS.panel,
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 14.5 }}>{ink}</Typography>
                    {allBack ? (
                      <CheckCircleRounded sx={{ color: TOKENS.good, fontSize: 26 }} />
                    ) : (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TOKENS.ink3 }}>
                        {back}/{tubs.length} back · <span style={{ color: TOKENS.accent }}>{tubs.length - back} used</span>
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                    {tubs.map((t) => {
                      const isBack = scanned.has(t.unit_code);
                      return (
                        <Chip key={t.unit_code} label={t.unit_code}
                          onClick={() => toggle(t.unit_code)}
                          icon={isBack ? <CheckCircleRounded sx={{ fontSize: 17 }} /> : undefined}
                          sx={{
                            height: 32, fontSize: 11.5, fontFamily: TOKENS.monoFont, fontWeight: 700,
                            cursor: 'pointer',
                            bgcolor: isBack ? `${TOKENS.good}1A` : `${TOKENS.accent}14`,
                            color: isBack ? TOKENS.good : TOKENS.accentStrong,
                            border: `1px solid ${isBack ? `${TOKENS.good}66` : `${TOKENS.accent}55`}`,
                            '& .MuiChip-icon': { color: TOKENS.good },
                          }} />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{
              p: 1.5, borderRadius: '12px',
              bgcolor: `${TOKENS.accent}12`, border: `1px solid ${TOKENS.accent}44`,
            }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TOKENS.ink2 }}>
                {scanned.size} back on the shelf<br />about to consume
              </Typography>
              <Typography sx={{ fontSize: 21, fontWeight: 800, color: TOKENS.accent, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {consumed.length} tub{consumed.length === 1 ? '' : 's'}<br />
                <span style={{ fontSize: 14 }}>~{fmtKg(consumedKg)}</span>
              </Typography>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button size="large" variant="contained" disabled={reconcile.isPending}
          startIcon={reconcile.isPending ? <CircularProgress size={18} color="inherit" /> : <AssignmentTurnedInRounded />}
          onClick={() => reconcile.mutate({
            mode: 'scan',
            returned_unit_codes: Array.from(scanned),
          })}
          sx={{ px: 3, fontWeight: 800, borderRadius: '12px',
            bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Book usage
        </Button>
      </DialogActions>
    </Dialog>
  );
}
